import { Box, Skeleton, type SxProps, Table, TableBody, TableCell, TableHead, TableRow, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { type FC, type ReactNode, useId } from "react";
import { ALPHA, FONT_SIZE, LETTER_SPACING, NEUTRAL, STATUS } from "../../../../theme/tokens";
import { CONFIG_TABLE_HEADER_CELL_SX } from "./configTableSx";

export type ConfigRowsPreviewColumn = {
	id: string;
	/** Already translated — the same header the editable table shows. */
	header: string;
};

export type ConfigRowsPreviewRow = {
	id: string;
	/** Display values keyed by column id; a column with no entry renders an empty cell. */
	cells: Readonly<Record<string, ReactNode>>;
	/** A short note about this row, drawn under it across every column (e.g. "changed since…"). */
	note?: ReactNode;
};

type Props = {
	/** Already translated — the title of the settings section the rows come from. */
	title: string;
	/** The section's own glyph, as its settings header shows it. */
	icon?: ReactNode;
	columns: readonly ConfigRowsPreviewColumn[];
	rows: readonly ConfigRowsPreviewRow[];
	/** Set (already translated) while the rows load: the table shows one placeholder row and announces this. */
	loadingLabel?: string;
	/** Notes under the table — a row that no longer exists, where the values came from. Already translated. */
	messages?: readonly ReactNode[];
	dataTestId?: string;
};

const TITLE_SX: SxProps<Theme> = (theme) => ({
	display: "flex",
	alignItems: "center",
	gap: 0.75,
	px: 1.5,
	pt: 1.25,
	pb: 0.75,
	color: theme.palette.primary.dark,
	fontSize: FONT_SIZE.LABEL_XS,
	fontWeight: 700,
	letterSpacing: LETTER_SPACING.LABEL,
	textTransform: "uppercase",
	"& svg": { fontSize: FONT_SIZE.EMPHASIS, color: theme.palette.primary.main },
});

/** The editable tables' header treatment, a size down: the preview is the same table, read at a glance. */
const HEADER_CELL_SX: SxProps<Theme> = (theme) => ({
	...CONFIG_TABLE_HEADER_CELL_SX(theme),
	py: 0.5,
	px: 1.25,
	fontSize: FONT_SIZE.LABEL_XS,
});

const BODY_CELL_SX = {
	py: 0.75,
	px: 1.25,
	fontSize: FONT_SIZE.CAPTION,
	color: "text.primary",
	whiteSpace: "nowrap",
	borderBottom: `1px solid ${NEUTRAL[150]}`,
	verticalAlign: "top",
} as const;

/** A row's note hangs off it, tinted like a warning chip: it qualifies the row above, it is not a row. */
const NOTE_CELL_SX: SxProps<Theme> = (theme) => ({
	py: 0.5,
	px: 1.25,
	fontSize: FONT_SIZE.LABEL_XS,
	fontWeight: 600,
	color: STATUS.WARNING.dark,
	backgroundColor: alpha(theme.palette.warning.main, ALPHA.HINT_TINT),
	borderBottom: `1px solid ${NEUTRAL[150]}`,
});

const MESSAGE_SX = { px: 1.5, py: 0.75, fontSize: FONT_SIZE.CAPTION, color: "text.secondary", lineHeight: 1.45 } as const;

const PreviewBody: FC<Pick<Props, "columns" | "rows" | "loadingLabel">> = ({ columns, rows, loadingLabel }) => {
	if (loadingLabel !== undefined) {
		return (
			<TableRow aria-busy="true">
				{columns.map((column, index) => (
					<TableCell key={column.id} sx={BODY_CELL_SX}>
						{index === 0 && <Box sx={visuallyHidden}>{loadingLabel}</Box>}
						<Skeleton variant="text" width="80%" />
					</TableCell>
				))}
			</TableRow>
		);
	}
	return rows.flatMap((row) => [
		<TableRow key={row.id} data-row-id={row.id}>
			{columns.map((column) => (
				<TableCell key={column.id} sx={BODY_CELL_SX}>
					{row.cells[column.id]}
				</TableCell>
			))}
		</TableRow>,
		...(row.note === undefined
			? []
			: [
					<TableRow key={`${row.id}-note`}>
						<TableCell colSpan={columns.length} sx={NOTE_CELL_SX}>
							{row.note}
						</TableCell>
					</TableRow>,
				]),
	]);
};

/**
 * A titled, compact, read-only view of configuration rows — the same columns and headers as the editable
 * settings table they come from, a size smaller, for showing "this is the setup" somewhere the setup is not
 * edited (a hover card, a confirmation, an audit view). Scrolls sideways within its container when the
 * columns outrun it. Rows are display-ready: the caller formats each value (and hides what the viewer may
 * not see). With no rows and no loading state, only the title and `messages` show.
 */
export const ConfigRowsPreview: FC<Props> = ({ title, icon, columns, rows, loadingLabel, messages, dataTestId }) => {
	const titleId = useId();
	const showTable = loadingLabel !== undefined || rows.length > 0;
	return (
		<Box component="section" data-testid={dataTestId} aria-labelledby={titleId}>
			<Typography component="h3" id={titleId} sx={TITLE_SX}>
				{icon}
				{title}
			</Typography>
			{showTable && (
				<Box sx={{ overflowX: "auto", mx: 1, border: `1px solid ${NEUTRAL[200]}`, borderRadius: 1 }}>
					<Table size="small" sx={{ minWidth: "max-content", width: "100%", "& tr:last-of-type > td": { borderBottom: "none" } }}>
						<TableHead>
							<TableRow>
								{columns.map((column) => (
									<TableCell key={column.id} sx={HEADER_CELL_SX}>
										{column.header}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							<PreviewBody columns={columns} rows={rows} loadingLabel={loadingLabel} />
						</TableBody>
					</Table>
				</Box>
			)}
			{messages?.map((message, index) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: messages are a fixed list per render; nothing reorders them.
				<Typography key={index} sx={MESSAGE_SX}>
					{message}
				</Typography>
			))}
		</Box>
	);
};
